import { FigureVar } from "./FigureVar";
import { FigureVarData, FigureVarSelectSettings, FigureVarSettings } from "./types";

export interface FigureVarsProps {
  id: string;
  dataPath: string;
  varSettings: FigureVarSettings[];
  properties: FigureVarData[];
}

/**
 * Renders a list of FigureVar components based on the provided variable settings and properties.
 *
 * @param props - Destructured parameter object
 * @param props.id - The id for the item
 * @param props.dataPath - The path to the array in figureInput (e.g., "dataGroups", "legend", "regressionLines")
 * @param props.varSettings - Array of variable settings to display
 * @param props.properties - Array of variable data for the item
 * @returns - An array of FigureVar components
 */
export const FigureVars = ({
  id,
  dataPath,
  varSettings,
  properties,
}: FigureVarsProps): React.ReactNode => {
  return varSettings.map((varSetting) => {
    const pix = properties.findIndex((p) => p.varName === varSetting.varName);
    // Return null if property is missing, should be added by parent component
    if (pix < 0) {
      return null;
    }

    // Check if this variable has conditional requirements
    if (varSetting.requires) {
      const requiredProperty = properties.find((p) => p.varName === varSetting.requires?.varName);
      // If the required property doesn't exist or its value is not in the allowed values, don't render
      if (!requiredProperty || !varSetting.requires.values.includes(requiredProperty.value)) {
        return null;
      }
    }

    return (
      <FigureVar
        key={`${id}-${varSetting.varName}`}
        pointer={`${dataPath}/${pix}/value`}
        figureVarType={varSetting.type}
        label={varSetting.label}
        varName={varSetting.varName}
        options={(varSetting as FigureVarSelectSettings).options}
      />
    );
  });
};
